import request, { HttpMethod } from '@app/libs/request';
import { WebErrorCode } from '@app/libs/request/httpConsts';
import { serverUrl } from '@app/config';
import { SupplierRegistrationProcessFormType } from '@app/domain/commonSupplierRegistration';
import { isDefined } from '@app/utils/utils';
import { guestHeaderGenerator } from '@app/utils/userIdentityHeadersGenerator';
import {
  API_VERSION_HEADER,
  ReducedSupplierRegistrationProcess,
  ReducedSupplierRegistrationProcessServerResponse,
  SupplierRegistrationProcessRequest,
  WEB_REFERRER_HEADER,
} from '@supplierRegistration/domain/supplierRegistration';
import { buildCaptchaHeaders } from '@supplierRegistration/services/captchaHeaders';
import { guestSession } from '@supplierRegistration/services/supplierRegistrationGuestSessionService';
import { SupplierRegistrationAsyncInitiateValidationProcessRequest } from '@supplierRegistration/domain/supplierRegistrationAsyncInitiateValidationProcess';
import { SupplierDocumentType } from '@supplierRegistration/stores/appStores/SupplierRegistrationDocumentsStore';
import { RawAxiosRequestHeaders } from 'axios';

export async function storeSupplierRegistrationProcessRequest(
  supplierData: SupplierRegistrationProcessRequest,
  validationType: SupplierRegistrationProcessFormType,
  language: string | undefined,
  captchaToken: string | undefined,
): Promise<ReducedSupplierRegistrationProcessServerResponse> {
  const headers: RawAxiosRequestHeaders = {
    [WEB_REFERRER_HEADER]: document.referrer,
    [API_VERSION_HEADER]: 'v2',
    ...buildCaptchaHeaders(captchaToken, 'store-validation'),
  };

  return request<ReducedSupplierRegistrationProcessServerResponse>(serverUrl, `/api/supplier-validation`, {
    data: supplierData,
    headers,
    params: { type: validationType, language: language },
    method: HttpMethod.post,
    dontRedirectToLogin: true,
    suppressNotification: true,
    errorsHandler: {
      default: {
        message: 'Could not request for supplier validation.',
      },
    },
  });
}

export async function storeSupplierRegistrationProcessRequestFromAsyncInitiateValidation(
  supplierData: SupplierRegistrationAsyncInitiateValidationProcessRequest,
  asyncProcessId: string,
  language: string | undefined,
  captchaToken: string | undefined,
): Promise<ReducedSupplierRegistrationProcessServerResponse> {
  const headers: RawAxiosRequestHeaders = {
    [WEB_REFERRER_HEADER]: document.referrer,
    [API_VERSION_HEADER]: 'v2',
    ...buildCaptchaHeaders(captchaToken, 'store-validation-async'),
  };

  return request<ReducedSupplierRegistrationProcessServerResponse>(serverUrl, `/api/supplier-validation/async/:asyncProcessId`, {
    data: supplierData,
    pathParams: { asyncProcessId },
    headers,
    params: { language },
    method: HttpMethod.post,
    dontRedirectToLogin: true,
    suppressNotification: true,
    errorsHandler: {
      default: {
        message: 'Could not request for supplier validation async.',
      },
    },
  });
}

function buildSupplierRegistrationDocumentsFormData(
  supplierData: SupplierRegistrationProcessRequest | SupplierRegistrationAsyncInitiateValidationProcessRequest,
  files: Record<SupplierDocumentType, File[]>,
): FormData {
  const formData = new FormData();

  formData.append('request', new Blob([JSON.stringify(supplierData)], { type: 'application/json' }));
  files[SupplierDocumentType.bankAccountDocumentation].forEach((file) => formData.append('bankAccountDocuments', file, file.name));
  files[SupplierDocumentType.taxIdCertificate].forEach((file) => formData.append('taxIdCertificateDocuments', file, file.name));

  return formData;
}

// The guest Authorization-Guest header never rides the plain JSON calls above (they're
// deliberately anonymous) -- supplierRegistration's InfraStores never wires guest headers into the
// app-wide dynamic-headers config (see supplierRegistrationGuestSessionService's own `login()`),
// so it has to be built and merged in explicitly here, the same way that service does.
async function buildSupplierRegistrationDocumentsHeaders(
  captchaToken: string | undefined,
  captchaContext: string,
): Promise<RawAxiosRequestHeaders> {
  return {
    [WEB_REFERRER_HEADER]: document.referrer,
    [API_VERSION_HEADER]: 'v2',
    ...buildCaptchaHeaders(captchaToken, captchaContext),
    ...(await guestHeaderGenerator(guestSession, null)),
  };
}

export async function storeSupplierRegistrationProcessWithDocuments(
  supplierData: SupplierRegistrationProcessRequest,
  files: Record<SupplierDocumentType, File[]>,
  validationType: SupplierRegistrationProcessFormType,
  language: string | undefined,
  captchaToken: string | undefined,
  onUploadProgress: (percent: number) => void,
): Promise<ReducedSupplierRegistrationProcessServerResponse> {
  return request<ReducedSupplierRegistrationProcessServerResponse>(serverUrl, `/api/supplier-validation/with-documents`, {
    data: buildSupplierRegistrationDocumentsFormData(supplierData, files),
    headers: await buildSupplierRegistrationDocumentsHeaders(captchaToken, 'store-validation'),
    params: { type: validationType, language },
    method: HttpMethod.post,
    isFileUpload: true,
    timeout: 300000,
    onUploadProgress,
    dontRedirectToLogin: true,
    suppressNotification: true,
    errorsHandler: {
      default: {
        message: 'Could not request for supplier validation.',
      },
    },
  });
}

export async function storeSupplierRegistrationProcessWithDocumentsFromAsyncInitiateValidation(
  supplierData: SupplierRegistrationAsyncInitiateValidationProcessRequest,
  files: Record<SupplierDocumentType, File[]>,
  asyncProcessId: string,
  language: string | undefined,
  captchaToken: string | undefined,
  onUploadProgress: (percent: number) => void,
): Promise<ReducedSupplierRegistrationProcessServerResponse> {
  return request<ReducedSupplierRegistrationProcessServerResponse>(
    serverUrl,
    `/api/supplier-validation/with-documents/async/:asyncProcessId`,
    {
      data: buildSupplierRegistrationDocumentsFormData(supplierData, files),
      pathParams: { asyncProcessId },
      headers: await buildSupplierRegistrationDocumentsHeaders(captchaToken, 'store-validation-async'),
      params: { language },
      method: HttpMethod.post,
      isFileUpload: true,
      timeout: 300000,
      onUploadProgress,
      dontRedirectToLogin: true,
      suppressNotification: true,
      errorsHandler: {
        default: {
          message: 'Could not request for supplier validation async.',
        },
      },
    },
  );
}

export interface DocumentFileErrorDTO {
  partName: string;
  fileIndex: number;
  originalFileName: string;
  errorCode: string;
}

// Mirrors SupplierValidationDocumentsRejectedDTO (server), returned as the 422 body when the
// with-documents endpoints reject one or more attached files.
export interface SupplierValidationDocumentsRejectedResponse {
  errors: DocumentFileErrorDTO[];
}

export function isSupplierValidationDocumentsRejectedResponse(
  body: unknown,
): body is SupplierValidationDocumentsRejectedResponse {
  return Array.isArray((body as Partial<SupplierValidationDocumentsRejectedResponse> | undefined)?.errors);
}

export interface DocumentFileErrorMapping {
  type: SupplierDocumentType;
  // Position among the *valid* files of this type that were actually sent -- see
  // SupplierRegistrationDocumentsStore.attachServerFileErrors for how this gets translated back to
  // a row in the full (client-errored rows included) filesByType[type] list.
  index: number;
  errorCode: string;
}

// The wire part names the server addresses files by (see MorteeExceptionHandler's
// SUPPLIER_VALIDATION_DOCUMENT_PART_NAMES_BY_TYPE) -- the reverse of the map the
// FormData builder above appends under.
const DOCUMENT_TYPE_BY_WIRE_PART_NAME: Record<string, SupplierDocumentType> = {
  bankAccountDocuments: SupplierDocumentType.bankAccountDocumentation,
  taxIdCertificateDocuments: SupplierDocumentType.taxIdCertificate,
};

export function mapDocumentFileErrorsToTypeAndIndex(
  rejectedResponse: SupplierValidationDocumentsRejectedResponse,
): DocumentFileErrorMapping[] {
  return rejectedResponse.errors
    .map((error): DocumentFileErrorMapping | undefined => {
      const type = DOCUMENT_TYPE_BY_WIRE_PART_NAME[error.partName];

      return type ? { type, index: error.fileIndex, errorCode: error.errorCode } : undefined;
    })
    .filter(isDefined);
}

// SUBMISSION_LIMIT_REACHED / DOCUMENTS_LIMIT_REACHED / EMAIL_MISMATCH are the three WebErrorCodes the
// with-documents endpoints can return outside the per-file 422 shape above (see MorteeWebErrorCode
// and MorteeExceptionHandler on the server) -- each maps to a dedicated modal message.
const DOCUMENT_SUBMISSION_ERROR_I18N_KEY: Partial<Record<WebErrorCode, string>> = {
  SUBMISSION_LIMIT_REACHED: 'supplierValidation.supplierRegister.documents.SUBMISSION_LIMIT_REACHED',
  DOCUMENTS_LIMIT_REACHED: 'supplierValidation.supplierRegister.documents.DOCUMENTS_LIMIT_REACHED',
  EMAIL_MISMATCH: 'supplierValidation.supplierRegister.documents.EMAIL_MISMATCH',
};

export function getDocumentSubmissionLimitErrorI18nKey(errorCode: string): string | undefined {
  return DOCUMENT_SUBMISSION_ERROR_I18N_KEY[errorCode as WebErrorCode];
}

export function transformToReducedSupplierRegistrationProcess(
  serverResponse: ReducedSupplierRegistrationProcessServerResponse,
): ReducedSupplierRegistrationProcess {
  return {
    registrationNumber: serverResponse.registrationNumber,
    displayInstructionType: serverResponse.displayInstructionType,
  };
}
